/* ============================================================
   collection-filters.js — <collection-filters>
   - Filter rail (drawer on mobile, sidebar on desktop)
   - Form submits via AJAX, re-renders only the results region
   - Sort dropdown changes URL + re-renders
   - URL state stays shareable
   ============================================================ */
(function () {
  if (customElements.get('collection-filters')) return;

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.results = this.querySelector('[data-results-target]');
      this.form    = this.querySelector('[data-filter-form]');
      this.rail    = this.querySelector('[data-filter-rail]');
      this.toggle  = this.querySelector('[data-filters-toggle]');
      this.close   = this.querySelector('[data-filters-close]');
      this.sort    = this.querySelector('[data-sort-select]');
      this.reset   = this.querySelector('[data-filter-reset]');
      this.activeCountPill = this.querySelector('[data-active-count]');

      // Rail visibility — desktop shows by default, mobile hidden until toggled.
      this._mql = window.matchMedia('(min-width: 990px)');
      this._applyRailVisibility();
      this._mql.addEventListener('change', () => this._applyRailVisibility());

      if (this.toggle) this.toggle.addEventListener('click', () => this._openRail());
      if (this.close)  this.close.addEventListener('click', () => this._closeRail());
      this.rail && this.rail.addEventListener('click', (e) => {
        if (e.target === this.rail) this._closeRail();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.rail && !this.rail.hidden && !this._mql.matches) this._closeRail();
      });

      if (this.form) {
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this._submit(); });
        // Auto-submit on checkbox change with a tiny debounce so multi-clicks batch
        this.form.addEventListener('change', () => this._debouncedSubmit());
        if (this.reset) this.reset.addEventListener('click', (e) => {
          e.preventDefault();
          this.form.querySelectorAll('input').forEach(i => {
            if (i.type === 'checkbox' || i.type === 'radio') i.checked = false;
            else i.value = '';
          });
          this._submit();
        });
      }

      if (this.sort) this.sort.addEventListener('change', () => this._submit());

      // Inline title search — filters visible cards client-side without a navigation
      this.searchInput  = this.querySelector('[data-collection-search]');
      this.searchClear  = this.querySelector('[data-collection-search-clear]');
      this._totalCards  = null;
      if (this.searchInput) {
        let st;
        const handler = () => {
          clearTimeout(st);
          st = setTimeout(() => this._filterBySearch(this.searchInput.value), 120);
        };
        this.searchInput.addEventListener('input', handler);
        this.searchInput.addEventListener('search', handler);
        if (this.searchClear) {
          this.searchClear.addEventListener('click', () => {
            this.searchInput.value = '';
            this._filterBySearch('');
            this.searchInput.focus();
          });
        }
      }

      this._updateActiveCount();
    }

    _filterBySearch(rawQuery) {
      if (!this.results) return;
      const query = (rawQuery || '').trim().toLowerCase();
      const items = this.results.querySelectorAll(':scope > ul > li');
      if (this._totalCards === null) this._totalCards = items.length;
      let visible = 0;
      items.forEach(li => {
        const title = (
          li.querySelector('.card-product__title')?.textContent ||
          li.dataset.title ||
          ''
        ).toLowerCase();
        const handle = (
          li.querySelector('[data-product-handle]')?.dataset.productHandle ||
          ''
        ).toLowerCase();
        const match = !query || title.includes(query) || handle.includes(query);
        li.classList.toggle('is-search-hidden', !match);
        if (match) visible++;
      });
      if (this.searchClear) this.searchClear.hidden = !query;
      this._updateBannerCount(visible);
    }

    _updateBannerCount(count) {
      const num = document.querySelector('[data-collection-count-num]');
      const label = document.querySelector('[data-collection-count-label]');
      if (num) num.textContent = String(count);
      if (label) label.textContent = count === 1 ? 'product' : 'products';
    }

    _applyRailVisibility() {
      if (!this.rail) return;
      if (this._mql.matches) {
        // Desktop: open + inline
        this.rail.hidden = false;
        this.rail.classList.remove('is-drawer-open');
      } else {
        // Mobile: hide by default
        this.rail.hidden = true;
      }
    }

    _openRail() {
      if (!this.rail) return;
      this.rail.hidden = false;
      requestAnimationFrame(() => this.rail.classList.add('is-drawer-open'));
      document.body.style.overflow = 'hidden';
      this.toggle && this.toggle.setAttribute('aria-expanded', 'true');
    }

    _closeRail() {
      if (!this.rail || this._mql.matches) return;
      this.rail.classList.remove('is-drawer-open');
      document.body.style.overflow = '';
      this.toggle && this.toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (!this._mql.matches) this.rail.hidden = true; }, 320);
    }

    _debouncedSubmit() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this._submit(), 300);
    }

    async _submit() {
      const params = new URLSearchParams();
      // Sort
      if (this.sort && this.sort.value) params.set('sort_by', this.sort.value);
      // Form fields
      if (this.form) {
        new FormData(this.form).forEach((v, k) => {
          if (v != null && String(v).length) params.append(k, v);
        });
      }
      const base = this.dataset.resultsUrl || window.location.pathname;
      const url = `${base}?${params.toString()}`;

      // Cancel any in-flight request so the latest click always wins
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      const signal = this._abort.signal;

      // Lock the results' current height so the grid doesn't collapse
      // when innerHTML is swapped → no layout jump, no page reflow.
      if (this.results) {
        const h = this.results.offsetHeight;
        if (h > 0) this.results.style.minHeight = h + 'px';
        this.results.classList.add('is-loading');
      }

      try {
        const res = await fetch(url, { headers: { Accept: 'text/html' }, signal });
        const html = await res.text();
        if (signal.aborted) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('collection-filters');
        if (!fresh) { window.location.href = url; return; }

        const freshResults = fresh.querySelector('[data-results-target]');
        if (freshResults && this.results) this.results.innerHTML = freshResults.innerHTML;

        // Update active-chip strip
        const oldChips = this.querySelector('.cf-active');
        const newChips = fresh.querySelector('.cf-active');
        if (oldChips && newChips) oldChips.replaceWith(newChips);
        else if (newChips) this.querySelector('.cf-toolbar').after(newChips);
        else if (oldChips) oldChips.remove();

        // Push URL
        window.history.replaceState({}, '', url);
        this._updateActiveCount();
        this._totalCards = null;

        // Update the banner product count from the freshly-rendered HTML
        const freshCount = doc.querySelector('[data-collection-count-num]');
        if (freshCount) {
          const n = parseInt(freshCount.textContent, 10);
          if (!Number.isNaN(n)) this._updateBannerCount(n);
        }

        // Re-apply current search query to the refreshed grid (overrides count)
        if (this.searchInput && this.searchInput.value) {
          this._filterBySearch(this.searchInput.value);
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        window.location.href = url;
      } finally {
        if (!signal.aborted && this.results) {
          this.results.classList.remove('is-loading');
          // Release the height lock on the next frame so the new content
          // gets a chance to lay out first — prevents the snap-back jump.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.results) this.results.style.minHeight = '';
            });
          });
        }
      }
    }

    _updateActiveCount() {
      if (!this.activeCountPill) return;
      const count = this.querySelectorAll('.cf-active .cf-chip:not(.cf-chip--clear)').length;
      this.activeCountPill.textContent = String(count);
      this.activeCountPill.hidden = count === 0;
    }
  }

  customElements.define('collection-filters', CollectionFilters);
})();
